import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { adminApi, apiError } from "../lib/api";
import { useAdminAuth } from "../context/AdminAuth";
import { useRefetchOnFocus } from "../hooks/useRefetchOnFocus";
import { ArrowLeft, Plus, Gift, Tag, Pencil, Trash2, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

const emptyReward = { name: "", description: "", points_cost: 100, active: true };
const emptyOffer = { title: "", description: "", active: true };

export default function AdminCatalog() {
  const nav = useNavigate();
  const { admin } = useAdminAuth();
  const [rewards, setRewards] = useState([]);
  const [offers, setOffers] = useState([]);
  const [dlg, setDlg] = useState(null); // {kind:'reward'|'offer', mode:'new'|'edit'}
  const [form, setForm] = useState(emptyReward);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [r, o] = await Promise.all([adminApi.get("/admin/rewards"), adminApi.get("/admin/offers")]);
      setRewards(r.data);
      setOffers(o.data);
    } catch (e) { toast.error(apiError(e)); }
  }, []);

  useEffect(() => {
    if (admin === false) nav("/admin");
    if (admin) load();
  }, [admin, load, nav]);
  useRefetchOnFocus(load);

  const openNew = (kind) => { setForm(kind === "reward" ? emptyReward : emptyOffer); setDlg({ kind, mode: "new" }); };
  const openEdit = (kind, item) => { setForm({ ...item }); setDlg({ kind, mode: "edit" }); };

  const save = async () => {
    setBusy(true);
    try {
      const base = dlg.kind === "reward" ? "/admin/rewards" : "/admin/offers";
      const payload = dlg.kind === "reward"
        ? { name: form.name, description: form.description, points_cost: parseInt(form.points_cost, 10) || 0, active: form.active }
        : { title: form.title, description: form.description, active: form.active };
      if (dlg.mode === "new") await adminApi.post(base, payload);
      else await adminApi.put(`${base}/${form.id}`, payload);
      toast.success("Gespeichert");
      setDlg(null);
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const del = async (kind, id) => {
    try {
      await adminApi.delete(`${kind === "reward" ? "/admin/rewards" : "/admin/offers"}/${id}`);
      toast.success("Gelöscht");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="min-h-screen bg-[#FAF7F2]">
      <header className="bg-[#4A1D24] text-stone-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex items-center gap-3">
          <button data-testid="catalog-back-button" onClick={() => nav("/admin")} className="p-2 rounded-lg hover:bg-white/10"><ArrowLeft className="h-5 w-5" /></button>
          <h1 className="font-serif text-2xl">Katalog verwalten</h1>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        <Tabs defaultValue="rewards">
          <TabsList data-testid="catalog-tabs">
            <TabsTrigger value="rewards" data-testid="tab-rewards"><Gift className="h-4 w-4 mr-1.5" /> Prämien</TabsTrigger>
            <TabsTrigger value="offers" data-testid="tab-offers"><Tag className="h-4 w-4 mr-1.5" /> Angebote</TabsTrigger>
          </TabsList>

          <TabsContent value="rewards" className="mt-5">
            <div className="flex justify-between items-center mb-4">
              <p className="text-sm text-stone-500">Prämien, die Kundinnen im Portal einlösen können.</p>
              <Button data-testid="add-reward-button" onClick={() => openNew("reward")} className="bg-[#4A1D24] hover:bg-[#3a161c] text-white h-9"><Plus className="h-4 w-4 mr-1" /> Prämie</Button>
            </div>
            <div className="grid sm:grid-cols-2 gap-3" data-testid="rewards-list">
              {rewards.map((r) => (
                <div key={r.id} data-testid={`reward-card-${r.id}`} className="rounded-2xl bg-white border border-stone-200/80 shadow-sm p-4 flex justify-between items-start">
                  <div className="min-w-0 pr-3">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-stone-900">{r.name}</p>
                      {!r.active && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-stone-100 text-stone-500 border border-stone-200">inaktiv</span>}
                    </div>
                    <p className="text-xs text-stone-500 mt-0.5">{r.description}</p>
                    <p className="text-sm font-mono text-[#4A1D24] mt-2">{r.points_cost} P</p>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <button data-testid={`edit-reward-${r.id}`} onClick={() => openEdit("reward", r)} className="p-2 rounded-lg hover:bg-stone-100 text-stone-500"><Pencil className="h-4 w-4" /></button>
                    <button data-testid={`delete-reward-${r.id}`} onClick={() => del("reward", r.id)} className="p-2 rounded-lg hover:bg-rose-50 text-rose-600"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="offers" className="mt-5">
            <div className="rounded-xl bg-amber-50 border border-amber-200 text-amber-900 p-3 mb-4 flex gap-2 text-sm">
              <Info className="h-4 w-4 mt-0.5 shrink-0" />
              <p>Angebote werden allen Kundinnen im <strong>Kundenportal</strong> angezeigt (kein E-Mail-Versand). So bleiben Mitteilungen einwilligungskonform.</p>
            </div>
            <div className="flex justify-between items-center mb-4">
              <p className="text-sm text-stone-500">Aktuelle Angebote im Kundenportal.</p>
              <Button data-testid="add-offer-button" onClick={() => openNew("offer")} className="bg-[#4A1D24] hover:bg-[#3a161c] text-white h-9"><Plus className="h-4 w-4 mr-1" /> Angebot</Button>
            </div>
            <div className="space-y-3" data-testid="offers-list">
              {offers.map((o) => (
                <div key={o.id} data-testid={`offer-card-${o.id}`} className="rounded-2xl bg-white border border-stone-200/80 shadow-sm p-4 flex justify-between items-start">
                  <div className="min-w-0 pr-3">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-stone-900">{o.title}</p>
                      {!o.active && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-stone-100 text-stone-500 border border-stone-200">inaktiv</span>}
                    </div>
                    <p className="text-xs text-stone-500 mt-0.5">{o.description}</p>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <button data-testid={`edit-offer-${o.id}`} onClick={() => openEdit("offer", o)} className="p-2 rounded-lg hover:bg-stone-100 text-stone-500"><Pencil className="h-4 w-4" /></button>
                    <button data-testid={`delete-offer-${o.id}`} onClick={() => del("offer", o.id)} className="p-2 rounded-lg hover:bg-rose-50 text-rose-600"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <Dialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">
              {dlg?.mode === "new" ? "Neu anlegen" : "Bearbeiten"} · {dlg?.kind === "reward" ? "Prämie" : "Angebot"}
            </DialogTitle>
            <DialogDescription>{dlg?.kind === "reward" ? "Prämie im Kundenportal." : "Angebot im Kundenportal."}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {dlg?.kind === "reward" ? (
              <>
                <div><Label className="text-xs">Name</Label><Input data-testid="catalog-name-input" value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
                <div><Label className="text-xs">Beschreibung</Label><Textarea data-testid="catalog-desc-input" value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
                <div><Label className="text-xs">Punktekosten</Label><Input data-testid="catalog-points-input" type="number" min="0" value={form.points_cost ?? ""} onChange={(e) => setForm({ ...form, points_cost: e.target.value })} /></div>
              </>
            ) : (
              <>
                <div><Label className="text-xs">Titel</Label><Input data-testid="catalog-title-input" value={form.title || ""} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
                <div><Label className="text-xs">Beschreibung</Label><Textarea data-testid="catalog-desc-input" value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
              </>
            )}
            <div className="flex items-center justify-between rounded-lg border border-stone-200 p-3">
              <span className="text-sm text-stone-600">Aktiv (im Portal sichtbar)</span>
              <Switch data-testid="catalog-active-switch" checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="catalog-save-button" onClick={save} disabled={busy} className="bg-[#4A1D24] hover:bg-[#3a161c] text-white">{busy ? "Speichern…" : "Speichern"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
